import { Avatar, IconButton, makeStyles, Tooltip, Typography } from "@material-ui/core";
import React, { CSSProperties } from "react";
import { blue900, lightGray, white } from "../constants";
import Badge from "../Badge/Badge";
import Button from "../Button/Button";
import { EditOutlined, ExitToApp } from "@material-ui/icons";
import type { GuardiaRow } from "./GuardiaTable";
import { getTransportAuthorizer } from "./utils/getTransportAuthorizer";
import { getEntryDock, getEntryTransportAuthorization } from "./utils/entryAuthorization";
import { getInitials } from "./utils/getInitials";
import { formatGuardiaFecha } from "./utils/formatDate";
import { getPersonPlates } from "./utils/personPlates";
import { PatenteChip } from "./VehicleUnitCells";
// Trae `.gv-plate` y `.gv-asociadas`, los estilos de patente que comparte con la vista por
// vehículo.
import "./GuardiaVehicleTable.css";

const guardiaStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: 16,
    backgroundColor: white,
    border: "1px solid #E0E0E0",
  },
  nameContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "12px",
  },
  nameInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },
  name: {
    fontWeight: 600,
    color: blue900,
    fontSize: 18,
  },
  id: {
    color: lightGray,
    fontSize: 16,
  },
  firstRowContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  badgeContainer: {
    alignSelf: "flex-start",
    height: "fit-content",
    display: "flex",
  },
  textItem: {
    color: lightGray,
    display: "flex",
    gap: 4,
    justifyContent: "space-between",
  },
  containerItem: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "center",
  },
  textContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
}));

interface GuardiaCardMobileProps {
  style?: CSSProperties;
  data: GuardiaRow;
  onMarkExit?: (row: GuardiaRow) => void;
  onEditDock?: (row: GuardiaRow) => void;
}

const GuardiaCardMobile = ({ style, data, onMarkExit, onEditDock }: GuardiaCardMobileProps) => {
  const classes = guardiaStyles();

  const getDate = formatGuardiaFecha(data.fecha);
  const entryDock = getEntryDock(data);
  const plates = getPersonPlates(data);
  // Un anfitrión de verdad siempre manda: el texto libre sólo aparece cuando no hay ninguno.
  const authorizer = data.host ? undefined : getTransportAuthorizer(data);
  const canEditDock = Boolean(data.eventId && getEntryTransportAuthorization(data));

  return (
    <div className={classes.root} style={style}>
      <div className={classes.firstRowContainer}>
        <div className={classes.nameContainer}>
          <Avatar style={{ backgroundColor: blue900 }}>{getInitials(data?.nombre, "")}</Avatar>
          <div className={classes.nameInfo}>
            <Typography className={classes.name}>{data.nombre}</Typography>
            <Typography className={classes.id}>DNI: {data.dni}</Typography>
          </div>
        </div>
        {/* <div className={classes.badgeContainer}>
          <Badge label={data.tipo} variant={TIPOS_GUARDIA_CARD[data.tipo].variant as any} />
        </div> */}
      </div>
      <div className={classes.textContainer}>
        <Typography className={classes.textItem}>
          Fecha y hora de entrada:{" "}
          <span style={{ fontWeight: 600, color: blue900 }}>
            {getDate.day} {getDate.time}
          </span>
        </Typography>
        <div className={classes.containerItem}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography className={classes.textItem}>Patente:</Typography>
            {/* Los mismos chips con punto de estado que la tabla, para no perder en mobile el dato
                de qué unidad sigue dentro. */}
            <div className="gv-asociadas gv-asociadas-mobile">
              {plates.length === 0 ? (
                <PatenteChip />
              ) : (
                plates.map((unit, index) => (
                  <PatenteChip
                    key={unit.plate}
                    patente={unit.plate}
                    inside={unit.inside}
                    className={index === 0 ? undefined : "gv-plate-trailer"}
                  />
                ))
              )}
            </div>
            {data.mainEvent && (
              <Typography variant="caption" style={{ color: lightGray, marginTop: -4 }}>
                (Acompañante)
              </Typography>
            )}
          </div>
          <Typography className={classes.textItem}>
            Empresa: <span style={{ fontWeight: 600, color: blue900 }}>{data.empresa}</span>
          </Typography>
        </div>
        {/* Sólo transportistas: en el resto de los flujos no hay empresa de transporte. */}
        {data.transportCompanyName && (
          <Typography className={classes.textItem}>
            Empresa de transporte:{" "}
            <span style={{ fontWeight: 600, color: blue900 }}>{data.transportCompanyName}</span>
          </Typography>
        )}
        {authorizer && (
          <Typography className={classes.textItem}>
            Autorizante: <span style={{ fontWeight: 600, color: blue900 }}>{authorizer}</span>
          </Typography>
        )}
        <div className={classes.containerItem}>
          <Typography className={classes.textItem}>
            Dock entrada: <span style={{ fontWeight: 600, color: blue900 }}>{entryDock ?? "-"}</span>
          </Typography>
          {canEditDock && (
            <Tooltip title="Editar dock asignado">
              <IconButton
                aria-label={`Editar dock asignado de ${data.nombre}`}
                className="dock-edit-button"
                size="small"
                onClick={() => onEditDock?.(data)}
              >
                <EditOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>
      <Button variant="outline-error" onClick={() => onMarkExit && onMarkExit(data)}>
        <ExitToApp fontSize="small" style={{ marginRight: 6 }} />
        Marcar salida
      </Button>
    </div>
  );
};

export default GuardiaCardMobile;
